import { defaultBlockRegistry } from '../renderer/block-registry.gen.js';
const WRITABLE = ['text', 'textarea', 'richtext'];
// A cyclic or absurdly deep `blocks` graph must not overflow the stack. Shrine's deepest real nesting
// (main-product → Gallery → blocks → collapsible-row → children) is ~5.
const MAX_BLOCK_DEPTH = 32;
/**
 * TWO GATES BEYOND THE CONTROL TYPE, both learned by measuring the real registry (2026-08-20): of 522
 * text-typed fields declared across sections and blocks, only 316 are prose.
 *
 *  1. `defaultKey` → an INTERFACE string owned by the 0050 dictionary ("Ajouter au panier", "Quantité",
 *     and — the one that would have hurt — `daysLabels` = "Lun, Mar, Mer, …"). 188 fields. They are
 *     already translated into every store language; rewriting them per store throws that away and, for
 *     the delimited ones, breaks the section. Excluded wholesale.
 *  2. `aiWritable: false` → a MACHINE FORMAT typed `text` (anchor id, colour list, separator, video URL,
 *     raw HTML). 18 occurrences from 8 declaration sites. `type` says how the control is drawn, never
 *     what the value means — this is the same trap collectIconNames documents for `iconColor`.
 */
export function writableTextFields(fields) {
    const out = [];
    for (const f of fields ?? []) {
        if (!f || !WRITABLE.includes(f.type))
            continue;
        if (f.aiWritable === false)
            continue;
        // `aiWritable: true` OVERRIDES the dictionary gate: a handful of dictionary-backed fields are
        // CONTENT placeholders («Titre de la colonne», «Pourquoi nous choisir»), not interface strings —
        // the 2026-08-21 from-scratch run shipped three cards titled «Titre de la colonne» because the
        // gate read them as 0050 dictionary labels. The schema author decides, field by field.
        if (f.defaultKey && f.aiWritable !== true)
            continue;
        out.push({
            fieldId: f.id,
            kind: f.type,
            label: f.label ?? f.id,
            // `maxLength` exists on text/textarea only; richtext has none. Read defensively — schemas are data.
            maxLength: f.maxLength,
        });
    }
    return out;
}
const indexCache = new WeakMap();
function textFieldIndex(sectionRegistry, blockRegistry) {
    const cached = indexCache.get(sectionRegistry);
    if (cached)
        return cached;
    const blocks = new Map();
    for (const [type, def] of Object.entries(blockRegistry)) {
        blocks.set(type, {
            name: def.schema.name ?? type,
            fields: writableTextFields(def.schema.fields),
        });
    }
    const sections = new Map();
    for (const [type, def] of Object.entries(sectionRegistry)) {
        const children = new Map();
        for (const child of def.schema.blocks ?? []) {
            children.set(child.type, {
                name: child.name ?? child.type,
                fields: writableTextFields(child.fields),
            });
        }
        sections.set(type, {
            name: def.schema.name ?? type,
            fields: writableTextFields(def.schema.fields),
            children,
        });
    }
    const index = { sections, blocks };
    indexCache.set(sectionRegistry, index);
    return index;
}
/** The fields a block of `type` may write, given the section it sits under and how deep it sits. */
function resolveBlock(index, sectionType, blockType, depth) {
    const inline = index.sections.get(sectionType)?.children.get(blockType);
    return depth === 0
        ? (inline ?? index.blocks.get(blockType))
        : (index.blocks.get(blockType) ?? inline);
}
/**
 * Collapse stored HTML to the plain text a model should read and write. THE MODEL NEVER SEES MARKUP:
 * feeding a richtext field's raw `<p>…<strong>…` as `current` teaches the model to answer in kind, and
 * `toRichtext` then ESCAPES that answer — the live bug of 2026-08-20, a hero headline reading
 * literally "<p>Vos oreilles <strong>libres</strong>". Text in, text out, on both sides of the call.
 */
export function htmlToPlainText(value) {
    return value
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(p|div|li|h[1-6])>/gi, '\n\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .replace(/&#0?39;/g, "'")
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}
/** True when a value carries actual markup (a tag pair or entity), not a stray `<` in prose. */
const HAS_MARKUP = /<\/?[a-z][^>]*>|&[a-z]+;|&#\d+;/i;
/** Read a stored value as plain text. Richtext is an object; everything else is already a string. */
function readAsText(value, kind) {
    if (kind === 'richtext') {
        const rt = value;
        return typeof rt?.html === 'string' ? htmlToPlainText(rt.html) : '';
    }
    return typeof value === 'string' ? value : '';
}
function pushSlots(meta, nodeType, settings, path, acc) {
    if (!meta)
        return;
    for (const f of meta.fields) {
        acc.push({
            path,
            fieldId: f.fieldId,
            nodeType,
            nodeName: meta.name,
            kind: f.kind,
            label: f.label,
            maxLength: f.maxLength,
            current: readAsText(settings?.[f.fieldId], f.kind),
        });
    }
}
function walkBlocks(blocks, parentPath, sectionType, index, acc, depth) {
    if (!Array.isArray(blocks) || depth > MAX_BLOCK_DEPTH)
        return;
    for (const block of blocks) {
        if (!block || typeof block !== 'object' || typeof block.id !== 'string')
            continue;
        const path = [...parentPath, block.id];
        pushSlots(resolveBlock(index, sectionType, block.type, depth), block.type, block.settings, path, acc);
        walkBlocks(block.blocks, path, sectionType, index, acc, depth + 1);
    }
}
/**
 * Every writable text slot in a document, in render order (sections top to bottom, blocks depth-first).
 *
 * Render order is not cosmetic: it is the order the construction scene reveals sections in, and the
 * order the model reads them in. A hero that arrives after the FAQ reads as a page written backwards.
 *
 * A `disabled` section or block is SKIPPED — it is not on the page, so writing copy into it would burn
 * credits on text no buyer will ever see.
 */
export function collectTextSlots(document, sectionRegistry, blockRegistry = defaultBlockRegistry) {
    const acc = [];
    try {
        const index = textFieldIndex(sectionRegistry, blockRegistry);
        const sections = document?.sections;
        if (!Array.isArray(sections))
            return acc;
        for (const section of sections) {
            if (!section || typeof section !== 'object' || typeof section.id !== 'string')
                continue;
            if (section.disabled)
                continue;
            const path = [section.id];
            pushSlots(index.sections.get(section.type), section.type, section.settings, path, acc);
            walkBlocks(section.blocks, path, section.type, index, acc, 0);
        }
    }
    catch {
        // Degrade to "nothing writable" rather than take down a generation mid-stream.
        return acc;
    }
    return acc;
}
/** Escape a plain-text run so it can be embedded in HTML without carrying markup across. */
function escapeHtml(text) {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}
/**
 * Wrap generated PLAIN TEXT as the richtext value shape, one paragraph per blank-line-separated run.
 *
 * THE MODEL NEVER SUPPLIES HTML (0059 lot 1). A richtext field is stored as HTML and rendered into the
 * storefront, so accepting model-authored markup would put a language model's output on the far side of
 * our sanitizer — an injection surface opened for a formatting nicety nobody asked for. Plain text in,
 * paragraphs out, escaped on the way. If a theme ever needs generated lists or bold, that is a separate
 * decision with a sanitizer in front of it, not a default.
 */
function toRichtext(text) {
    const paragraphs = text
        .split(/\n{2,}/)
        .map((p) => p.trim())
        .filter(Boolean)
        .map((p) => `<p>${escapeHtml(p).replace(/\n/g, '<br>')}</p>`);
    return { format: 'html', html: paragraphs.join('') };
}
/**
 * Walk to the node an address names, carrying the SECTION TYPE and depth along — both are needed to
 * resolve which fields that node may write (see `textFieldIndex`: `column` means six different things).
 */
function findNode(document, path) {
    const [sectionId, ...blockIds] = path;
    const section = document.sections?.find((s) => s?.id === sectionId);
    if (!section)
        return undefined;
    if (blockIds.length === 0) {
        section.settings ??= {};
        return { type: section.type, settings: section.settings, sectionType: section.type, depth: -1 };
    }
    let node;
    let children = section.blocks;
    for (const id of blockIds) {
        node = children?.find((b) => b?.id === id);
        if (!node)
            return undefined;
        children = node.blocks;
    }
    if (!node)
        return undefined;
    node.settings ??= {};
    return {
        type: node.type,
        settings: node.settings,
        sectionType: section.type,
        depth: blockIds.length - 1,
    };
}
/**
 * Write generated values into a document at their addresses, returning a NEW document.
 *
 * PURE, AND HOSTILE TO ITS INPUT. Patches come back from a language model, so an address may not exist,
 * may point at a colour, or may carry 400 characters for a 60-character button. Every one of those is a
 * DROP with a reason, never a throw and never a silent write: the generation keeps going with the
 * theme's own copy in that slot (the safety net of 0059 §5.3 — the page is presentable at every
 * instant), and the caller can both tell the seller and decline to charge for what did not land.
 *
 * `maxLength` is REJECTED, NOT TRUNCATED. A headline cut mid-word is worse than the theme's default
 * headline, and it would be the seller's problem to discover on their live page.
 */
export function applyTextSlots(document, patches, sectionRegistry, blockRegistry = defaultBlockRegistry) {
    const index = textFieldIndex(sectionRegistry, blockRegistry);
    const rejected = [];
    // Structured clone of the tree we touch. Cheap next to an API call, and it keeps the caller's copy
    // intact so a failed generation can be abandoned by simply dropping the result.
    const next = JSON.parse(JSON.stringify(document));
    let applied = 0;
    for (const patch of patches) {
        if (!patch || !Array.isArray(patch.path) || patch.path.length === 0) {
            rejected.push({ patch, reason: 'unknown-address' });
            continue;
        }
        // A model that answers with markup anyway gets it stripped, not rendered: on a text field the
        // tags would print literally; on richtext they would be escaped into visible "<p>" (see above).
        const raw = typeof patch.value === 'string' ? patch.value : '';
        const value = (HAS_MARKUP.test(raw) ? htmlToPlainText(raw) : raw).trim();
        if (!value) {
            // An empty generation must leave the theme's copy standing, never blank the field.
            rejected.push({ patch, reason: 'empty' });
            continue;
        }
        const node = findNode(next, patch.path);
        if (!node) {
            rejected.push({ patch, reason: 'unknown-address' });
            continue;
        }
        const meta = node.depth < 0
            ? index.sections.get(node.sectionType)
            : resolveBlock(index, node.sectionType, node.type, node.depth);
        const field = meta?.fields.find((f) => f.fieldId === patch.fieldId);
        if (!field) {
            rejected.push({ patch, reason: 'not-writable' });
            continue;
        }
        if (field.maxLength !== undefined && value.length > field.maxLength) {
            rejected.push({ patch, reason: 'too-long' });
            continue;
        }
        node.settings[patch.fieldId] = field.kind === 'richtext' ? toRichtext(value) : value;
        applied += 1;
    }
    return { document: next, applied, rejected };
}
//# sourceMappingURL=textSlots.js.map